import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';
import { themeForRoom } from '../../lib/classroomTheme';

// Staff self-service schedule. Read-only view of my own posted hours plus
// the full team schedule for the week, so any staff member can see when
// they're on and who else is scheduled. Backed by GET /api/me/schedule,
// which only returns a week to non-management once it's been published.

// ── time / date helpers (mirror the admin builder's formatting) ──────
const formatTime = (t) => {
  if (!t) return '—';
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
};

function mondayOf(date) {
  const d = new Date(date);
  const dow = d.getDay(); // 0 Sun … 6 Sat
  const back = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - back);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

const rangeLabel = (weekStart) => {
  const end = addDays(weekStart, 4); // Mon → Fri
  const opts = { month: 'short', day: 'numeric' };
  return `${weekStart.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`;
};

// ── how a single day cell reads for staff ────────────────────────────
// Returns { text, working, tone }. `working` cells render the time range
// (bold, colored); everything else is a muted status word.
function cellDisplay(cell) {
  if (!cell) return { text: 'Off', working: false, tone: 'off' };
  const s = cell.status;
  if (s === 'center_closed' || s === 'holiday') {
    return { text: cell.closure_name || 'Center closed', working: false, tone: 'closed' };
  }
  if (s === 'call_off') return { text: 'Called off', working: false, tone: 'absent' };
  if (s === 'sick') return { text: 'Sick', working: false, tone: 'absent' };
  if (s === 'time_off_approved') return { text: 'Time off', working: false, tone: 'timeoff' };
  if (s === 'time_off_pending') return { text: 'Time off (pending)', working: false, tone: 'timeoff' };
  if (s === 'off' || s === 'none') return { text: 'Off', working: false, tone: 'off' };

  // working / swap / anything with real hours
  if (Array.isArray(cell.segments) && cell.segments.length) {
    return {
      text: cell.segments.map((seg) => `${formatTime(seg.start)}–${formatTime(seg.end)}`).join(', '),
      working: true,
      tone: 'working',
    };
  }
  if (cell.start_time && cell.end_time) {
    return { text: `${formatTime(cell.start_time)}–${formatTime(cell.end_time)}`, working: true, tone: 'working' };
  }
  return { text: 'Off', working: false, tone: 'off' };
}

const TONE_TEXT = {
  working: 'text-gray-900 font-semibold',
  off: 'text-gray-300',
  closed: 'text-purple-600',
  absent: 'text-red-500',
  timeoff: 'text-blue-500',
};

// segment classroom (if split) else the cell's room
function cellRoom(cell) {
  if (!cell) return null;
  if (Array.isArray(cell.segments) && cell.segments.length) {
    const rooms = [...new Set(cell.segments.map((s) => s.classroom).filter(Boolean))];
    return rooms.join(' → ') || cell.classroom || null;
  }
  return cell.classroom || null;
}

export default function MySchedulePage() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('me'); // 'me' | 'team'
  const [classrooms, setClassrooms] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/me/schedule?start=${toISO(weekStart)}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load your schedule'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weekStart]);

  // Classroom colors (same source + palette as the director's Team Schedule).
  useEffect(() => {
    let cancelled = false;
    api.get('/classrooms')
      .then(({ data }) => { if (!cancelled) setClassrooms(data || []); })
      .catch(() => { /* color-coding is a nice-to-have; grid still renders */ });
    return () => { cancelled = true; };
  }, []);

  const days = data?.days || [];
  const me = data?.me || null;
  const team = data?.team || [];
  const published = data?.published;

  const isThisWeek = useMemo(
    () => toISO(weekStart) === toISO(mondayOf(new Date())),
    [weekStart]
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDaysIcon className="w-6 h-6" /> My Schedule
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Your posted hours and the full team schedule. Something look off? Ask your director — they build the schedule.
          </p>
        </div>

        {/* Week navigator */}
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 py-2 mb-4">
          <button
            onClick={() => setWeekStart((w) => mondayOf(addDays(w, -7)))}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            aria-label="Previous week"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="text-sm font-semibold text-gray-900">{rangeLabel(weekStart)}</div>
            <button
              onClick={() => setWeekStart(mondayOf(new Date()))}
              disabled={isThisWeek}
              className={`text-xs ${isThisWeek ? 'text-gray-400' : 'text-orange-600 hover:underline'}`}
            >
              {isThisWeek ? 'This week' : 'Jump to this week'}
            </button>
          </div>
          <button
            onClick={() => setWeekStart((w) => mondayOf(addDays(w, 7)))}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            aria-label="Next week"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-4 w-full sm:w-auto sm:inline-flex">
          {[
            { key: 'me', label: 'My Hours' },
            { key: 'team', label: 'Full Staff Schedule' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg transition ${
                tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : published === false ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
            <CalendarDaysIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">This week's schedule hasn't been posted yet.</p>
            <p className="text-sm text-gray-500 mt-1">
              You'll get an email when your director publishes it. Check back soon.
            </p>
          </div>
        ) : tab === 'me' ? (
          <MyHours me={me} days={days} classrooms={classrooms} />
        ) : (
          <TeamGrid team={team} days={days} classrooms={classrooms} />
        )}
      </div>
    </div>
  );
}

// ── My Hours: one card per weekday ───────────────────────────────────
function MyHours({ me, days, classrooms }) {
  if (!me) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
        <ClockIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-700 font-medium">You're not on the schedule this week.</p>
        <p className="text-sm text-gray-500 mt-1">If that's a surprise, check with your director.</p>
      </div>
    );
  }

  const cellByDate = new Map((me.cells || []).map((c) => [c.date, c]));

  return (
    <div>
      <div className="bg-orange-50 border border-orange-100 rounded-2xl px-5 py-4 mb-4 flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-orange-700">Scheduled this week</div>
          <div className="text-2xl font-bold text-gray-900">{me.total_hours} hrs</div>
        </div>
        <ClockIcon className="w-8 h-8 text-orange-400" />
      </div>

      <div className="space-y-3">
        {days.map((d) => {
          const cell = cellByDate.get(d.iso);
          const disp = cellDisplay(cell);
          const room = disp.working ? cellRoom(cell) : null;
          const partial = cell?.partial_time_off || [];
          return (
            <div key={d.iso} className="bg-white border border-gray-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">{d.label}</div>
                <div className="text-xs text-gray-400">
                  {new Date(`${d.iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              </div>
              <div className="text-right min-w-0">
                <div className={`text-base ${TONE_TEXT[disp.tone] || 'text-gray-700'}`}>{disp.text}</div>
                {room && (
                  <div className={`inline-block text-[11px] font-medium mt-1 px-2 py-0.5 rounded-full border ${themeForRoom(room, classrooms).pill}`}>
                    {room}
                  </div>
                )}
                {partial.map((p, i) => (
                  <div key={i} className="text-xs text-amber-600 mt-0.5">
                    ⚠ Off {formatTime(p.start)}–{formatTime(p.end)}
                    {p.status === 'pending' ? ' (pending)' : ''}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Full staff schedule: scrollable name × day grid ──────────────────
function TeamGrid({ team, days, classrooms }) {
  if (!team.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center text-gray-500">
        No staff scheduled this week.
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="sticky left-0 z-10 bg-gray-50 text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                Staff
              </th>
              {days.map((d) => (
                <th key={d.iso} className="px-3 py-3 font-semibold text-gray-600 text-center whitespace-nowrap min-w-[110px]">
                  <div>{d.label.slice(0, 3)}</div>
                  <div className="text-[11px] font-normal text-gray-400">
                    {new Date(`${d.iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {team.map((row) => {
              const cellByDate = new Map((row.cells || []).map((c) => [c.date, c]));
              return (
                <tr
                  key={row.staff_id}
                  className={`border-b border-gray-100 last:border-0 ${row.is_self ? 'bg-orange-50' : ''}`}
                >
                  <th
                    scope="row"
                    className={`sticky left-0 z-10 text-left px-4 py-3 font-medium whitespace-nowrap ${
                      row.is_self ? 'bg-orange-50 text-orange-900' : 'bg-white text-gray-800'
                    }`}
                  >
                    {row.name}
                    {row.is_self && (
                      <span className="ml-1.5 text-[10px] font-semibold uppercase text-orange-600">You</span>
                    )}
                  </th>
                  {days.map((d) => {
                    const disp = cellDisplay(cellByDate.get(d.iso));
                    const room = disp.working ? cellRoom(cellByDate.get(d.iso)) : null;
                    const theme = disp.working && room ? themeForRoom(room, classrooms) : null;
                    return (
                      <td key={d.iso} className="px-2 py-2 text-center align-top">
                        <div className={`rounded-lg px-1.5 py-1.5 border ${theme ? theme.head : 'border-transparent'}`}>
                          <div className={`${disp.working ? 'text-gray-900 font-medium' : TONE_TEXT[disp.tone] || 'text-gray-400'}`}>
                            {disp.text}
                          </div>
                          {room && <div className="text-[11px] text-gray-700/80 mt-0.5">{room}</div>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
